import { useState } from 'react';

import { SortableTag } from './components/SortableTag/SortableTag'
import { SortableTagContent } from './components/SortableTagContent'
import { SortableTagsContainer } from './components/SortableTagsContainer'
import { SortableTagsStyled } from './styles/SortableTagsStyled'
import {
  type SortableTagsCommonProps,
  type SortableTagsComponents,
} from './types'

export const Components: SortableTagsComponents = {
  TagList: SortableTagsContainer,
  TagItem: SortableTag,
  TagContent: SortableTagContent,
}

export interface SortableTagItemType<TagValue = any> {
  value: TagValue
}

export type SortableTagsLayoutType = 'x' | 'y' | 'xy'

export interface ISortableTagsProps<TagValue = any> {
  initialTags?: SortableTagItemType<TagValue>[]
  tags?: SortableTagItemType<TagValue>[]
  handleTagReorder?: (
    reorderedTags: SortableTagItemType<TagValue>[],
    oldIndex: number,
    newIndex: number
  ) => void
  tagWidth?: string
  layout?: SortableTagsLayoutType
  showDragHandle?: boolean
  showOrderNumber?: boolean
  components?: Partial<SortableTagsComponents>
}

export const ControlledSortableTags = <TagValue,>({
  tags,
  handleTagReorder = () => undefined,
  tagWidth,
  layout = 'xy',
  showDragHandle = true,
  showOrderNumber = true,
  components,
}: Omit<ISortableTagsProps<TagValue>, 'initialTags'>) => {
  const ExtendedComponents: SortableTagsComponents = {
    ...Components,
    ...components,
  }

  const onSort = ({ oldIndex, newIndex }) => {
    const tagsCopy = [...tags]

    const item = tagsCopy.splice(oldIndex, 1)[0]

    tagsCopy.splice(newIndex, 0, item)

    handleTagReorder(tagsCopy, oldIndex, newIndex)
  }

  const commonProps: SortableTagsCommonProps = {
    sortableTagsProps: {
      tags,
      handleTagReorder,
      tagWidth,
      layout,
      showDragHandle,
      showOrderNumber,
      components: ExtendedComponents,
    },
    innerProps: {},
    onSort,
  }

  return (
    <SortableTagsStyled layout={layout}>
      <ExtendedComponents.TagList {...commonProps} />
    </SortableTagsStyled>
  )
}

export const UncontrolledSortableTags = <TagValue,>({
  initialTags,
  ...otherProps
}: Omit<ISortableTagsProps<TagValue>, 'tags'>) => {
  const [tags, setTags] = useState(initialTags)

  return (
    <ControlledSortableTags
      tags={tags}
      handleTagReorder={setTags}
      {...otherProps}
    />
  )
}

export const SortableTags = <TagValue,>({
  initialTags = [],
  tags,
  handleTagReorder = null,
  ...otherProps
}: ISortableTagsProps<TagValue>) => {
  return tags ? (
    <ControlledSortableTags
      tags={tags}
      handleTagReorder={handleTagReorder}
      {...otherProps}
    />
  ) : (
    <UncontrolledSortableTags initialTags={initialTags} {...otherProps} />
  )
}
